import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// Avatar emojis only — display names are resolved via t(`avatars.<id>`)
// in render. Keys must match those defined in JoinScreen.AVATARS.
const AVATAR_EMOJI = {
  smile: '😀', cool: '😎', star: '🤩', party: '🥳', cat: '😺',
  fox: '🦊', penguin: '🐧', sparkle: '🌟', robot: '🤖', alien: '👽',
  unicorn: '🦄', bear: '🐻', dog: '🐶', octopus: '🐙', butterfly: '🦋',
  dragon: '🐉', astronaut: '🚀', wizard: '🧙',
};

function waitRange(seconds) {
  const mid = Math.round(seconds / 60);
  const lo = Math.max(1, mid - 2);
  const hi = mid + 3;
  return { min: lo, max: hi };
}

function GraceCountdown({ expiresAtMs }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000)));

  useEffect(() => {
    const id = setInterval(() => {
      const r = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
      setRemaining(r);
      if (r === 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAtMs]);

  const m = Math.floor(remaining / 60);
  const s = String(remaining % 60).padStart(2, '0');
  return <span>{t('graceHold.expiresIn', { time: `${m}:${s}` })}</span>;
}

// Shared AudioContext — created on first user gesture to work on mobile
let sharedAudioCtx = null;
function getAudioContext() {
  if (!sharedAudioCtx) sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (sharedAudioCtx.state === 'suspended') sharedAudioCtx.resume();
  return sharedAudioCtx;
}

// Play a prominent alert tone using Web Audio API
function playAlertSound() {
  try {
    const ctx = getAudioContext();
    const playBeep = (time, freq, duration = 0.35, vol = 0.5) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(vol, time);
      gain.gain.exponentialRampToValueAtTime(0.01, time + duration);
      osc.start(time);
      osc.stop(time + duration);
    };
    const t = ctx.currentTime;
    // Melody: C5-E5-G5-C6 (notification style)
    playBeep(t, 523, 0.2, 0.5);
    playBeep(t + 0.25, 659, 0.2, 0.5);
    playBeep(t + 0.5, 784, 0.2, 0.5);
    playBeep(t + 0.75, 1047, 0.4, 0.6);
    // Repeat after 2.5 seconds
    setTimeout(() => {
      try {
        const t2 = ctx.currentTime;
        playBeep(t2, 523, 0.2, 0.5);
        playBeep(t2 + 0.25, 659, 0.2, 0.5);
        playBeep(t2 + 0.5, 784, 0.2, 0.5);
        playBeep(t2 + 0.75, 1047, 0.4, 0.6);
      } catch {}
    }, 2500);
  } catch {}
}

// Pre-warm audio context on user interaction (for mobile)
export function warmAudioContext() {
  try { getAudioContext(); } catch {}
}

// Fire a local browser notification as fallback
function fireLocalNotification(title, body) {
  try {
    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/favicon.ico' });
    }
  } catch {}
}

function formatTicketDateTime(ms, locale) {
  const d = ms ? new Date(ms) : new Date();
  // Browser-localized: month name follows the active i18n language.
  return d.toLocaleString(locale || undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function QueueScreen({ phase, myEntry, graceHold, deskLabel, session, onLeave, calledTitle, calledBody }) {
  const { t, i18n } = useTranslation();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const calledNotified = useRef(false);

  // Alert when called: sound + vibration + browser notification
  useEffect(() => {
    if (phase === 'called' && !calledNotified.current) {
      calledNotified.current = true;
      playAlertSound();
      fireLocalNotification(
        calledTitle || t('notifications.calledTitle'),
        calledBody || t('notifications.calledBody'),
      );
      if (navigator.vibrate) navigator.vibrate([300, 200, 300, 200, 300]);
    }
    if (phase !== 'called') calledNotified.current = false;
  }, [phase, calledTitle, calledBody, t]);

  const avatarId = session?.avatarId;
  const avatar = avatarId && AVATAR_EMOJI[avatarId]
    ? { emoji: AVATAR_EMOJI[avatarId], name: t(`avatars.${avatarId}`) }
    : { emoji: '😀', name: t('avatars.guest') };

  // ── Called screen ─────────────────────────────────────────────────────────
  if (phase === 'called') {
    return (
      <div style={{ ...s.card, textAlign: 'center' }}>
        <style>{`
          @keyframes vq-pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.15); }
          }
          @keyframes vq-glow {
            0%, 100% { box-shadow: 0 0 20px rgba(16,185,129,0.3); }
            50% { box-shadow: 0 0 40px rgba(16,185,129,0.6); }
          }
        `}</style>
        <div style={{
          width: 120, height: 120, borderRadius: '50%',
          background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'vq-pulse 1.5s ease-in-out infinite, vq-glow 1.5s ease-in-out infinite',
        }}>
          <span style={{ fontSize: '4rem' }}>🔔</span>
        </div>
        <h1 style={{ ...s.title, color: '#059669', fontSize: '2rem' }}>{calledTitle || t('called.title')}</h1>
        <p style={{ ...s.subtitle, fontSize: '1.05rem' }}>{calledBody || t('called.body')}</p>
        {deskLabel && (
          <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#06B6D4' }}>
            {t('called.goToDesk', { desk: deskLabel })}
          </p>
        )}
      </div>
    );
  }

  // ── Grace-hold screen ─────────────────────────────────────────────────────
  if (phase === 'grace-hold') {
    return (
      <div style={{ ...s.card, textAlign: 'center' }}>
        <div style={{ fontSize: '4rem' }}>⏳</div>
        <h1 style={s.title}>{t('graceHold.title')}</h1>
        <p style={s.subtitle}>{t('graceHold.body')}</p>
        {graceHold && (
          <p style={{ fontWeight: 700, fontSize: '1.2rem', color: '#F59E0B' }}>
            <GraceCountdown expiresAtMs={graceHold.expiresAtMs} />
          </p>
        )}
        <button type="button" onClick={onLeave} style={s.dangerBtn}>
          {t('queue.leaveButton')}
        </button>
      </div>
    );
  }

  // ── In-queue screen ───────────────────────────────────────────────────────
  const { min, max } = myEntry ? waitRange(myEntry.estimatedWaitSeconds) : { min: '—', max: '—' };

  return (
    <div style={s.card}>
      {/* Avatar + name + ticket number */}
      <div style={s.header}>
        <span style={{ fontSize: '2.5rem' }}>{avatar.emoji}</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#F8FAFC', lineHeight: 1 }}>
            {avatar.name}
          </span>
          <span style={{ fontSize: '0.78rem', color: '#94A3B8', letterSpacing: '0.04em' }}>
            {t('queue.ticketShort', { number: myEntry?.ticketNumber ?? '—' })} &middot; {formatTicketDateTime(myEntry?.joinedAtMs, i18n.language)}
          </span>
        </div>
      </div>

      {/* Position */}
      <div style={s.positionBox}>
        <p style={s.positionLabel}>{t('queue.position')}</p>
        <p style={s.positionNumber}>
          #{myEntry?.position ?? '—'}
        </p>
        <p style={s.positionLabel}>{t('queue.positionSuffix')}</p>
      </div>

      {/* Wait estimate */}
      <div style={s.infoRow}>
        <span style={s.infoLabel}>{t('queue.waitLabel')}</span>
        <span style={s.infoValue}>
          {myEntry ? t('queue.waitRange', { min, max }) : '—'}
        </span>
      </div>

      {/* Ticket number (permanent daily ID) */}
      <div style={s.pinBox}>
        <p style={s.pinLabel}>{t('queue.ticketLabel')}</p>
        <p style={s.pinValue}>#{myEntry?.ticketNumber ?? '—'}</p>
        <p style={s.pinHint}>{t('queue.ticketHint')}</p>
      </div>

      {/* Recovery PIN */}
      {session?.pin && (
        <div style={s.recoveryPinBox}>
          <p style={{ ...s.pinLabel, color: '#A78BFA' }}>{t('queue.pinLabel')}</p>
          <p style={{ ...s.pinValue, color: '#A78BFA' }}>{session.pin}</p>
          <p style={{ ...s.pinHint, color: '#94A3B8' }}>{t('queue.pinHint')}</p>
        </div>
      )}

      {/* Leave */}
      <button type="button" onClick={() => setShowLeaveModal(true)} style={s.dangerBtn}>
        {t('queue.leaveButton')}
      </button>

      {/* Leave confirmation modal */}
      {showLeaveModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>{t('queue.leaveConfirmTitle')}</h2>
            <p style={{ margin: '0 0 16px', color: '#94A3B8', fontSize: '0.9rem' }}>
              {t('queue.leaveConfirmBodySimple')}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => { setShowLeaveModal(false); onLeave(); }} style={{ ...s.dangerBtn, flex: 1 }}>
                {t('queue.leaveConfirmButton')}
              </button>
              <button type="button" onClick={() => setShowLeaveModal(false)} style={{ ...s.secondaryBtn, flex: 1 }}>
                {t('queue.leaveCancelButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  card: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 20, width: '100%',
  },
  title: { fontSize: '1.75rem', fontWeight: 700, margin: 0, color: '#F8FAFC' },
  subtitle: { fontSize: '0.95rem', color: '#94A3B8', margin: 0, textAlign: 'center' },
  header: { display: 'flex', alignItems: 'center', gap: 10 },
  positionBox: {
    background: 'rgba(6,182,212,0.12)', borderRadius: 16, padding: '24px 40px',
    textAlign: 'center', width: '100%', boxSizing: 'border-box',
  },
  positionLabel: { margin: 0, fontSize: '0.875rem', color: '#94A3B8', fontWeight: 500 },
  positionNumber: { margin: '4px 0', fontSize: '4rem', fontWeight: 800, color: '#06B6D4' },
  infoRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', background: '#1E293B', borderRadius: 10, padding: '12px 16px',
    boxSizing: 'border-box',
  },
  infoLabel: { color: '#94A3B8', fontSize: '0.9rem' },
  infoValue: { fontWeight: 600, color: '#F8FAFC', fontSize: '0.95rem' },
  pinBox: {
    background: '#FFF7ED', border: '1.5px dashed #FDBA74', borderRadius: 12,
    padding: '16px 24px', textAlign: 'center', width: '100%', boxSizing: 'border-box',
  },
  pinLabel: { margin: '0 0 4px', fontSize: '0.8rem', color: '#92400E', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  pinValue: { margin: '0 0 6px', fontSize: '2.25rem', fontWeight: 800, color: '#92400E', letterSpacing: '0.3em' },
  pinHint: { margin: 0, fontSize: '0.75rem', color: '#B45309' },
  recoveryPinBox: {
    background: 'rgba(124,58,237,0.08)', border: '1.5px dashed #7C3AED', borderRadius: 12,
    padding: '16px 24px', textAlign: 'center', width: '100%', boxSizing: 'border-box',
    marginTop: 8,
  },
  dangerBtn: {
    fontSize: '0.95rem', fontWeight: 600, padding: '12px', borderRadius: 12,
    border: 'none', background: '#FEE2E2', color: '#DC2626', cursor: 'pointer',
    width: '100%',
  },
  secondaryBtn: {
    fontSize: '0.95rem', fontWeight: 600, padding: '12px', borderRadius: 12,
    border: '1px solid #334155', background: '#1E293B', color: '#94A3B8', cursor: 'pointer',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20, zIndex: 200,
  },
  modal: {
    background: '#1E293B', border: '1px solid #334155', borderRadius: 16, padding: 24,
    width: '100%', maxWidth: 360,
  },
  input: {
    fontSize: '1rem', padding: '12px 14px', borderRadius: 10,
    border: '1px solid #334155', outline: 'none', width: '100%',
    boxSizing: 'border-box', background: '#1E293B', color: '#F8FAFC',
  },
  inputError: { border: '1.5px solid #EF4444' },
  errorMsg: { margin: '0 0 8px', fontSize: '0.8rem', color: '#EF4444' },
};
